import type { RefineThemedLayoutHeaderProps as RefineThemedLayoutV2HeaderProps } from "@refinedev/antd";
import { useGetIdentity, useLogout } from "@refinedev/core";
import {
  Layout as AntdLayout,
  Avatar,
  Space,
  Switch,
  theme,
  Typography,
  Dropdown,
  Button,
  type MenuProps,
} from "antd";
import {
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  DashboardOutlined,
  HeartOutlined,
} from "@ant-design/icons";
import React, { useContext } from "react";
import { ColorModeContext } from "../../contexts/color-mode";
import { useNavigate } from "react-router";

const { Text } = Typography;
const { useToken } = theme;

type IUser = {
  id: number;
  name: string;
  avatar: string;
  email: string;
  isStaff: boolean;
  isSuperuser: boolean;
};

export const Header: React.FC<RefineThemedLayoutV2HeaderProps> = ({
  sticky = true,
}) => {
  const { token } = useToken();
  const { data: user } = useGetIdentity<IUser>();
  const { mode, setMode } = useContext(ColorModeContext);
  const { mutate: logout } = useLogout();
  const navigate = useNavigate();

  const headerStyles: React.CSSProperties = {
    backgroundColor: token.colorBgElevated,
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: "0px 24px",
    height: "64px",
  };

  if (sticky) {
    headerStyles.position = "sticky";
    headerStyles.top = 0;
    headerStyles.zIndex = 1;
  }

  const menuItems: MenuProps['items'] = [
    ...(user ? [{
      key: 'profile',
      label: 'Profile Settings',
      icon: <SettingOutlined />,
      onClick: () => navigate('/me'),
    }] : []),
    ...(user?.isStaff ? [{
      key: 'health',
      label: 'System Health',
      icon: <HeartOutlined />,
      onClick: () => navigate('/health'),
    }] : []),
    ...(user?.isStaff ? [{
      key: 'admin',
      label: 'Admin Panel',
      icon: <DashboardOutlined />,
      onClick: () => window.open('/admin/', '_blank'),
    }] : []),
    ...(user ? [{
      type: 'divider' as const,
    }] : []),
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      onClick: () => logout(),
    },
  ];

  const userButton = (
    <Button
      type="text"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        height: "auto",
        padding: "4px 8px",
      }}
    >
      <Text strong style={{ marginRight: "4px" }}>
        {user?.name || 'User'}
      </Text>
      <Avatar 
        src={user?.avatar} 
        alt={user?.name}
        icon={!user?.avatar && <UserOutlined />}
        size="small"
      />
    </Button>
  );

  return (
    <AntdLayout.Header style={headerStyles}>
      <Space>
        <Switch
          checkedChildren="🌛"
          unCheckedChildren="🔆"
          onChange={() => setMode(mode === "light" ? "dark" : "light")}
          defaultChecked={mode === "dark"}
        />
        <Dropdown 
          menu={{ items: menuItems }}
          placement="bottomRight"
          trigger={['click']}
        >
          {userButton}
        </Dropdown>
      </Space>
    </AntdLayout.Header>
  );
};
